import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import type { ExperienceCredits } from "@/types";

const EXPERIENCE_CREDITS_VALUE = "Experience Credits";
const EXPERIENCE_CREDITS_ICON = "collections_bookmark";

function pluralizeCredits(credits: number): string {
  return `${credits} ${credits === 1 ? "Credit" : "Credits"}`;
}

function experienceCreditsBreakdownFallback(total: number): string {
  return total === 0 ? "No experience credits have been recorded yet" : "No credit breakdown is available";
}

function buildExperienceCreditLines(experienceCredits: ExperienceCredits) {
  return [
    experienceCredits.educationCredits
      ? {
          id: "education",
          description: "Academic Degree",
          credits: experienceCredits.educationCredits
        }
      : null,
    experienceCredits.employmentCredits
      ? {
          id: "employment",
          description: "Employment",
          credits: experienceCredits.employmentCredits
        }
      : null
  ].filter((line): line is { id: string; description: string; credits: number } => line !== null);
}

function buildExperienceCreditsTooltip(experienceCredits: ExperienceCredits) {
  const creditLines = buildExperienceCreditLines(experienceCredits);

  return {
    title: "Experience Credits",
    supportingText: (
      <div className="flex w-full flex-col items-start gap-1 text-body-medium text-sys-brand-on-surface-variant">
        <div>Total Credits: {experienceCredits.total}</div>
        {creditLines.length ? (
          <ul className="list-disc pl-5">
            {creditLines.map((line) => (
              <li key={line.id}>
                {line.description} - {pluralizeCredits(line.credits)}
              </li>
            ))}
          </ul>
        ) : (
          <div>{experienceCreditsBreakdownFallback(experienceCredits.total)}</div>
        )}
      </div>
    )
  };
}

function buildStatusLabel(experienceCredits: ExperienceCredits): string {
  if (experienceCredits.complete === true) {
    return "Completed";
  }

  if (experienceCredits.complete == null || experienceCredits.threshold == null) {
    return `${experienceCredits.total} credits`;
  }

  return `${experienceCredits.total} of ${experienceCredits.threshold}`;
}

export function buildExperienceCreditsMetric(
  experienceCredits?: ExperienceCredits | null,
  options?: { scrollToId?: string; onClick?: () => void; programStarted?: boolean }
): MetricCardType<Path> {
  if (!experienceCredits || options?.programStarted === false) {
    return {
      label: "Not Started",
      value: EXPERIENCE_CREDITS_VALUE,
      iconName: EXPERIENCE_CREDITS_ICON,
      ...(options?.onClick && { onClick: options.onClick }),
      ...(options?.scrollToId && { scrollToId: options.scrollToId })
    };
  }

  const variant =
    experienceCredits.complete == null || experienceCredits.threshold == null
      ? "info"
      : experienceCredits.complete
        ? "success"
        : "warn";

  return {
    label: buildStatusLabel(experienceCredits),
    value: EXPERIENCE_CREDITS_VALUE,
    iconName: EXPERIENCE_CREDITS_ICON,
    variant,
    tooltip: buildExperienceCreditsTooltip(experienceCredits),
    ...(options?.onClick && { onClick: options.onClick }),
    ...(options?.scrollToId && { scrollToId: options.scrollToId })
  };
}
